import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ShieldCheck, ShieldAlert, User as UserIcon, Briefcase, GraduationCap, Calendar, Clock, IdCard, CheckCircle2, XCircle } from "lucide-react";

/**
 * Public verification page for instructor credentials. Target of the QR code
 * printed on the physical badge. No authentication required.
 */
export default function VerifyInstructor() {
  const { id } = useParams();               // credential_id (e.g. OMPEC-IC-000001)
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/verify-instructor/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Credencial no encontrada"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-marino/60">Verificando credencial...</div>;

  if (err) {
    return (
      <div data-testid="verify-instructor-error" className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden border border-red-200">
          <div className="bg-red-600 text-white px-6 py-5 flex items-center gap-3">
            <ShieldAlert className="w-8 h-8" />
            <div>
              <div className="font-heading font-bold text-lg">Credencial no encontrada</div>
              <div className="text-sm text-white/80">{err}</div>
            </div>
          </div>
          <div className="p-6 text-marino/70 text-sm">
            Si crees que esto es un error, verifica que el código QR o el ID de credencial sean correctos.
          </div>
        </div>
      </div>
    );
  }

  const valid = data.valid;
  return (
    <div className="min-h-screen bg-marino/5 flex items-center justify-center p-4 sm:p-6">
      <div data-testid="verify-instructor-card" className="max-w-lg w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Status header */}
        <div className={`px-6 py-5 flex items-center gap-3 text-white ${valid ? "bg-marino" : "bg-red-600"}`}>
          {valid ? <ShieldCheck className="w-10 h-10 text-naranja" /> : <XCircle className="w-10 h-10" />}
          <div>
            <div className="font-heading font-bold text-xl">
              {valid ? "Credencial válida" : "Credencial vencida"}
            </div>
            <div className="text-sm text-white/80">OM-PEC · Centro de Entrenamiento</div>
          </div>
        </div>

        {/* Instructor info */}
        <div className="p-6 space-y-5">
          <div className="flex items-start gap-3">
            <UserIcon className="w-5 h-5 text-naranja mt-1 flex-shrink-0" />
            <div>
              <div className="text-[11px] uppercase tracking-wider text-marino/60 font-semibold">Instructor Cualificado</div>
              <div className="text-marino font-heading font-bold text-2xl mt-0.5">{data.full_name}</div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            <Row icon={IdCard} label="CURP" value={data.curp} mono />
            <Row icon={Briefcase} label="Puesto" value={data.puesto} />
            <div className="sm:col-span-2">
              <Row icon={GraduationCap} label="Especialidad" value={data.especialidad} />
            </div>
            <Row icon={Calendar} label="Fecha de emisión" value={formatDate(data.issue_date)} />
            <Row icon={Clock} label="Vigencia" value={formatDate(data.valid_until)} highlight={!valid} />
            <div className="sm:col-span-2">
              <Row icon={ShieldCheck} label="ID de credencial" value={data.credential_id} mono />
            </div>
          </div>

          <div className={`text-xs px-3 py-2 rounded-lg mt-4 border ${valid ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-700"} inline-flex items-center gap-2`}>
            {valid ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {valid ? "Autenticidad verificada" : "Esta credencial ha expirado"}
          </div>
        </div>

        <div className="bg-marino/5 px-6 py-3 text-[11px] text-marino/60 text-center border-t border-gris/20">
          Sistema de verificación de credenciales OM-PEC
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value, mono = false, highlight = false }) => (
  <div className="flex items-start gap-2.5">
    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${highlight ? "text-red-600" : "text-marino/60"}`} />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-marino/60 font-semibold">{label}</div>
      <div className={`${mono ? "font-mono text-sm" : "text-sm"} ${highlight ? "text-red-600 font-semibold" : "text-marino"} break-words`}>{value}</div>
    </div>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
};
